import { grandfatheredCoverageAMinPercent } from '@ourbranch/farm-utils';
import { FarmRiskType } from '@ourbranch/lookups';
import { appConfig } from 'brand-config';
import { COVERAGE_LIMIT_OVERRIDE_HELP_TEXT } from 'common/helpers/yup-helpers';

/**
 * Generates Coverage A help text based on brand config business rules, ACV/RCE values,
 * and the "Adjust dwelling limit more" toggle state.
 *
 * Used by FO1, FO2, and FO3 risk types:
 * - FO1 and FO2-ACV: ACV-based validation (mobile home per-sqft or percentage of ACV)
 * - FO2-RC: RCE-based validation with year-built-aware minimum (80% for Functional RC dwellings
 *   built ≤1950 since #35312, grandfathered down to the limit an existing term already carries)
 * - FO3: RCE-based validation with 90% minimum (100% while Expanded Replacement Cost is active)
 *
 * The range is always anchored on the stored 360Value RCE (`building.replacementCostEstimate.total`),
 * never on the saved Coverage A: an agent may lower Coverage A inside the band, and quoting that
 * lowered limit as "100% RCE" contradicts the RCE card rendered above the field (#35804).
 *
 * Issue #28846: Coverage A is now always editable with validation
 * Issue #29003: Added RCE-based brand config support with year-built overrides for FO2 RC
 * Issue #35312: Functional RC (≤1950) floor raised 50% → 80%, grandfathered on existing terms
 * Issue #33567: Expanded RC requires an FO3 to be insured to 100% of RCE
 * Issue #35804: FO3 switched to this helper; hint mirrors the validation floors
 *
 * @param isExistingTerm true on the policy route, where `initialCoverageA` is the limit the term is
 *   already bound at. Offers pass it falsy: new business gets the untightened floor.
 * @param showOverrideRules true for a user with the limit-override authority (#35868): while the risk's
 *   Coverage A is overridden they see the override rules instead of a range that contradicts the value
 *   they just saved. Everyone else keeps the standard range.
 */
export function getCoverageAHelpText({
  item,
  state,
  riskType,
  initialCoverageA,
  isExistingTerm = false,
  showOverrideRules = false
}) {
  if (showOverrideRules && item?.coverages?.limitOverride) {
    return COVERAGE_LIMIT_OVERRIDE_HELP_TEXT;
  }
  const propertyType = item?.building?.propertyType;
  const acv = item?.building?.actualCashValueCalculation?.total;
  const rce = item?.building?.replacementCostEstimate?.total;
  const sqFt = item?.building?.sqFt;
  const lossSettlement = item?.coverages?.lossSettlement;
  const canAdjustMore = item.coverages?.canEditCoverageAmount;

  const businessRules = appConfig.farm?.[state]?.risks[riskType]?.businessRules?.coverageA;
  // #33567: Expanded Replacement Cost requires an FO3 to be insured to 100% of RCE. Mirrors the
  // validator in yup-helpers.js so the hint never promises a 90% floor the form will reject.
  const isExpandedReplacementCost = riskType === FarmRiskType.Fo3 && Boolean(item?.coverages?.replacementCostExp);

  // If brand has ACV-based business rules and ACV loss settlement is selected
  if (businessRules && lossSettlement === 'ACV') {
    // Mobile Home: minimum per sq ft, max of ACV or RCE
    if (businessRules.mobileHome?.propertyTypes?.includes(propertyType) && sqFt && acv) {
      const minValue = sqFt * businessRules.mobileHome.minPerSqFt;
      const maxValue = Math.max(acv, rce || 0);
      if (canAdjustMore) {
        return `Minimum: $${minValue.toLocaleString()} (based on ${sqFt} sq ft at $${businessRules.mobileHome.minPerSqFt}/sq ft). No maximum with justification.`;
      }
      return `You may adjust this value from $${minValue.toLocaleString()} (based on ${sqFt} sq ft at $${businessRules.mobileHome.minPerSqFt}/sq ft) to $${maxValue.toLocaleString()} (greater of ACV or RCE)`;
    }
    // Home/Modular/Barndominium: percentage of ACV, max of ACV or RCE
    if (businessRules.acvBasedStructures?.propertyTypes?.includes(propertyType) && acv) {
      const minValue = Math.round(acv * businessRules.acvBasedStructures.minPercent);
      const maxValue = Math.max(acv, rce || 0);
      if (canAdjustMore) {
        return `Minimum: $${minValue.toLocaleString()} (${businessRules.acvBasedStructures.minPercent * 100}% ACV). No maximum with justification.`;
      }
      return `You may adjust this value from $${minValue.toLocaleString()} (${businessRules.acvBasedStructures.minPercent * 100}% ACV) to $${maxValue.toLocaleString()} (greater of ACV or RCE)`;
    }
  }

  // RCE-based help text: use brand config rceBasedStructures if available (GitHub issue #29003)
  if (businessRules?.rceBasedStructures && rce && lossSettlement !== 'ACV') {
    const rceRules = businessRules.rceBasedStructures;
    const yearBuilt = item?.building?.yearBuilt;
    let minPercent = rceRules.minPercent;

    // Check year-built overrides (e.g., 80% for homes built 1950 or before)
    if (rceRules.yearBuiltOverrides && yearBuilt && yearBuilt <= rceRules.yearBuiltOverrides.threshold) {
      minPercent = grandfatheredCoverageAMinPercent({
        yearBuiltOverrides: rceRules.yearBuiltOverrides,
        isExistingTerm,
        priorCoverageA: initialCoverageA,
        rce
      });
    }

    if (isExpandedReplacementCost) {
      minPercent = Math.max(minPercent, 1);
    }

    const minValue = Math.round(rce * minPercent);
    const maxValue = Math.round(rce * rceRules.maxPercent);
    // A grandfathered floor is a dollar limit, not a filed percentage, so its share of RCE is
    // rarely a whole number — round the label rather than printing "61.728% RCE".
    const minPercentLabel = Math.round(minPercent * 100);
    if (canAdjustMore) {
      return `Minimum: $${minValue.toLocaleString()} (${minPercentLabel}% RCE). No maximum with justification.`;
    }
    return `You may adjust this from $${minValue.toLocaleString()} (${minPercentLabel}% RCE) to $${maxValue.toLocaleString()} (${rceRules.maxPercent * 100}% RCE).`;
  }

  // Default fallback for states/brands without `rceBasedStructures` rules (Forreston only configures
  // FO3 for IL). Same shape as the validator's fallback in yup-helpers.js: anchor on the stored RCE
  // when there is one and only fall back to the saved Coverage A when no RCE has been stored yet
  // (new risk, failed 360Value pull); FO3 floors at 90% (100% under Expanded RC), others at 80%.
  const fallbackRce = rce || initialCoverageA;
  const fallbackMinPercent = isExpandedReplacementCost ? 1 : riskType === FarmRiskType.Fo3 ? 0.9 : 0.8;
  const fallbackMinValue = Math.round(fallbackRce * fallbackMinPercent);
  const fallbackMinLabel = `${Math.round(fallbackMinPercent * 100)}% RCE`;
  if (canAdjustMore) {
    return `Minimum: $${fallbackMinValue.toLocaleString()} (${fallbackMinLabel}). No maximum with justification.`;
  }
  return `You may adjust this from $${fallbackMinValue.toLocaleString()} (${fallbackMinLabel}) to $${Math.round(fallbackRce).toLocaleString()} (100% RCE).`;
}
